import type { Duration, Instant } from '@js-joda/core';
import {
  ChronoUnit,
  convert,
  DateTimeFormatter,
  LocalDate,
  LocalDateTime,
  LocalTime,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { Locale } from '@js-joda/locale';
import '@js-joda/locale_en-us';
import type { OdProtosCommon_Date as GqlDate } from '__generated__/graphql';
import type { LocalDateOrLocalDateTime } from 'sellDirect/SSEAContext';
import { assertHasValue } from 'shared/helpers/assertHasValue';

/**
 * Formats a time like "11:59 PM".
 * @public
 */
export const TIME_ONLY_FORMAT = DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US);

/**
 * Formats a time like "11:59 PM PT".
 *
 * For some time zones like "America/Phoenix", js-joda will directly output "America Phoenix".
 * TODO: Figure out how to format it into MT/PT?
 * @public
 */
export const TIME_WITH_TZ_FORMAT = DateTimeFormatter.ofPattern('h:mm a z').withLocale(Locale.US);

/**
 * Gets the timezone abbreviation (e.g., "PT", "MST", "CT") for a given ZoneId.
 * Uses Intl.DateTimeFormat which properly handles edge cases like America/Phoenix.
 * Normalizes DST-specific abbreviations (PDT/PST) to generic ones (PT) for consistency.
 * @param zoneId - The ZoneId to get the abbreviation for
 * @returns Timezone abbreviation (e.g., "PT", "MST", "CT")
 */
export const getTimezoneAbbreviation = (time: LocalDateTime, zoneId: ZoneId): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: zoneId.id(),
    timeZoneName: 'short',
  });

  // Format a date and extract just the timezone part
  const parts = formatter.formatToParts(convert(time).toDate());
  const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
  const tz = timeZonePart?.value || '';

  return tz;
};

/**
 * Formats a date like "Sun 11:59 PM".
 * @public
 */
export const DAY_OF_WEEK_AND_TIME = DateTimeFormatter.ofPattern('EEE h:mm a').withLocale(Locale.US);

/** Formats a date like "Jan 1". */
export const MONTH_DAY_FORMAT = DateTimeFormatter.ofPattern('MMM d').withLocale(Locale.US);

/**
 * Formats a date like "Sun, Jan 1".
 * @public
 */
export const MONTH_DAY_AND_DAY_OF_WEEK_FORMAT = DateTimeFormatter.ofPattern(
  'EEE, MMM d',
).withLocale(Locale.US);

/**
 * Formats a date like "Jan".
 * @public
 */
export const MONTH_ONLY_FORMAT = DateTimeFormatter.ofPattern('MMM').withLocale(Locale.US);

/**
 * Formats a date like "Jan 1, 2022".
 * @public
 */
export const SHORTER_FULL_DATE_FORMAT = DateTimeFormatter.ofPattern('MMM d, yyyy').withLocale(
  Locale.US,
);

/** Formats a date like "January 1, 2022". */
export const FULL_DATE_FORMAT = DateTimeFormatter.ofPattern('MMMM d, yyyy').withLocale(Locale.US);

/** Formats a date like "Sunday, January 1, 2022". */
export const FULL_DATE_AND_DAY_OF_WEEK_FORMAT = DateTimeFormatter.ofPattern(
  'EEEE, MMMM d, yyyy',
).withLocale(Locale.US);

const MONTH_DAY_TIME_FORMAT_WITHOUT_TIME_ZONE = DateTimeFormatter.ofPattern(
  'MMM d, h:mm a',
).withLocale(Locale.US);

/**
 * Formats a ZonedDateTime like "Jan 15, 10:30 AM"
 * @param dateTime - The ZonedDateTime to format
 * @returns Formatted date string in English
 * @public
 */
export const formatDate = (dateTime: ZonedDateTime): string => {
  return dateTime.format(MONTH_DAY_TIME_FORMAT_WITHOUT_TIME_ZONE);
};

/** Converts a GraphQL Date to a {@link LocalDate}. */
export const localDateFromGqlDate = ({ year, month, day }: GqlDate) => {
  return LocalDate.of(year, month, day);
};

/** Converts most ISO-8601 strings to {@link LocalDate} or errors if input is null-ish. */
export function localDateFromString(input: string | null | undefined): LocalDate {
  return LocalDate.parse(assertHasValue('input', input));
}

/** Converts most ISO-8601 strings to {@link LocalDate} or null if input is null-ish. */
export function localDateFromStringOrNull(input: string | null | undefined): LocalDate | null {
  if (!input) {
    return null;
  }
  return LocalDate.parse(input);
}

/**
 * Converts a {@link LocalDate} to a GQL date.
 * @public
 */
export const localDateToGqlDate = (date: LocalDate): GqlDate => {
  return {
    __typename: 'OdProtosCommon_Date',
    year: date.year(),
    month: date.monthValue(),
    day: date.dayOfMonth(),
  };
};

/**
 * Converts most ISO-8601 strings to {@link Instant} or errors if input is null-ish.
 * @public
 */
export function instantFromString(input: string | null | undefined): Instant {
  // Don't use Instant.parse, which only parses ISO-8601 strings in UTC time (i.e. end in "Z").
  return ZonedDateTime.parse(assertHasValue('input', input)).toInstant();
}

/** Converts most ISO-8601 strings to {@link Instant} or null if input is null-ish. */
export function instantFromStringOrNull(input: string | null | undefined): Instant | null {
  if (!input) {
    return null;
  }
  // Don't use Instant.parse, which only parses ISO-8601 strings in UTC time (i.e. end in "Z").
  return ZonedDateTime.parse(input).toInstant();
}

/** Converts most ISO-8601 strings to {@link ZonedDateTime} or errors if input is null-ish. */
export function zonedDateTimeFromString(
  input: string | null | undefined,
  zoneId: ZoneId,
): ZonedDateTime {
  return ZonedDateTime.parse(assertHasValue('input', input)).withZoneSameInstant(zoneId);
}

/** Converts most ISO-8601 strings to {@link ZonedDateTime} or null if input is null-ish. */
export function zonedDateTimeFromStringOrNull(
  input: string | null | undefined,
  zoneId: ZoneId,
): ZonedDateTime | null {
  if (!input) {
    return null;
  }
  return ZonedDateTime.parse(input).withZoneSameInstant(zoneId);
}

/**
 * Converts most ISO-8601 strings to {@link ZonedDateTime} in default system zone
 * or errors if input is null-ish.
 */
export function systemZonedDateTimeFromString(input: string | null | undefined): ZonedDateTime {
  return ZonedDateTime.parse(assertHasValue('input', input)).withZoneSameInstant(
    ZoneId.systemDefault(),
  );
}

/**
 * Converts most ISO-8601 strings to {@link ZonedDateTime} in default system zone
 * or null if input is null-ish.
 * @public
 */
export function systemZonedDateTimeFromStringOrNull(
  input: string | null | undefined,
): ZonedDateTime | null {
  if (!input) {
    return null;
  }
  return ZonedDateTime.parse(input).withZoneSameInstant(ZoneId.systemDefault());
}

const MINUTE_S = ChronoUnit.MINUTES.duration().seconds();
const HOUR_S = ChronoUnit.HOURS.duration().seconds();
const DAY_S = ChronoUnit.DAYS.duration().seconds();
const MONTH_S = ChronoUnit.MONTHS.duration().seconds();
const YEAR_S = ChronoUnit.YEARS.duration().seconds();

/**
 * Formats a {@link Duration} into a human-friendly text that approximates the duration.
 * @public
 */
export function formatApproxDuration(duration: Duration) {
  const seconds = duration.seconds();
  if (seconds > 2 * YEAR_S) {
    return `${Math.floor(seconds / YEAR_S)} years`;
  } else if (seconds >= YEAR_S) {
    return 'a year';
  } else if (seconds >= 2 * MONTH_S) {
    return `${Math.floor(seconds / MONTH_S)} months`;
  } else if (seconds >= MONTH_S) {
    return 'a month';
  } else if (seconds >= 2 * DAY_S) {
    return `${Math.floor(seconds / DAY_S)} days`;
  } else if (seconds >= DAY_S) {
    return 'a day';
  } else if (seconds >= 2 * HOUR_S) {
    return `${Math.floor(seconds / HOUR_S)} hours`;
  } else if (seconds >= HOUR_S) {
    return 'an hour';
  } else if (seconds >= 2 * MINUTE_S) {
    return `${Math.floor(seconds / MINUTE_S)} minutes`;
  } else if (seconds >= MINUTE_S) {
    return 'a minute';
  }

  return 'a few seconds';
}

type FormatDateAsDaysUntilOptions = {
  /** The future date being counted toward. */
  date: LocalDate;
  /** Day the count starts from. Must be resolved in the same zone as `date`. */
  today: LocalDate;
};

/** Format the date as "X days", or the empty string once `date` is no longer ahead of `today`. */
export function formatDateAsDaysUntil({ date, today }: FormatDateAsDaysUntilOptions) {
  const daysDifference = today.until(date, ChronoUnit.DAYS);
  if (daysDifference <= 0) {
    return '';
  } else if (daysDifference === 1) {
    return '1 day';
  }
  return `${daysDifference} days`;
}

/**
 * Formats a date with month, day, and year (e.g., "Jan 1, 2022")
 * @param dateTime - The ZonedDateTime to format
 * @returns Formatted date string
 */
export const formatLocalizedDateWithYear = (dateTime: ZonedDateTime): string => {
  return dateTime.format(SHORTER_FULL_DATE_FORMAT);
};

/**
 * Formats a date with day of week and month/day in English (e.g., "Tue, Jul 8")
 * @param dateTime - The LocalDateOrLocalDateTime to format
 * @returns Formatted date string in English
 * @public
 */
export const formatEnglishDateWithDayOfWeek = (dateTime: LocalDate | LocalDateTime): string => {
  return dateTime.format(MONTH_DAY_AND_DAY_OF_WEEK_FORMAT);
};

type AssessmentTimeRange = 'slot' | 'two-hour' | 'four-hour';

/**
 * Formats a complete date with day of week, date, and time range
 * @param params.date - The LocalDateOrLocalDateTime to use for the date part
 * @param params.time - The LocalDateOrLocalDateTime to use for the time part
 * @param params.zoneId - Optional ZoneId to format as PT, CT, etc.
 * @param params.timeRange - Whether this is a specific time slot or time range
 * @param params.showArrivalPrefix - Whether to show the arrival prefix string (e.g., "Scheduled: ")
 * @returns Formatted date string
 */
export const formatDateWithTimeRange = ({
  date,
  time,
  zoneId,
  timeRange,
  showArrivalPrefix: showScheduledPrefix,
}: {
  date: LocalDate | LocalDateTime;
  time: LocalDateOrLocalDateTime;
  zoneId?: ZoneId;
  timeRange: AssessmentTimeRange;
  showArrivalPrefix: boolean;
}): string => {
  const formattedTimeString = getExteriorAssessmentTimeslotFromLocalTime(time, timeRange);

  let timeZonePart = '';
  if (zoneId) {
    if (time instanceof LocalDateTime) {
      timeZonePart = ` ${getTimezoneAbbreviation(time, zoneId)}`;
    } else if (time instanceof LocalDate) {
      // TODO: This needs to be fixed properly. You cannot talk about timezone
      // without specific time, so this is a hack for now to unblock CI. We'll
      // just assume that the timezone at noon is the one we want.
      timeZonePart = ` ${getTimezoneAbbreviation(time.atTime(LocalTime.NOON), zoneId)}`;
    }
  }

  const timeConnector = timeRange === 'slot' ? ' at ' : ' from ';
  const datePart = formatEnglishDateWithDayOfWeek(date);
  const prefix = showScheduledPrefix ? 'Scheduled: ' : '';
  return `${prefix}${datePart}${timeConnector}${formattedTimeString}${timeZonePart}`;
};

/** Based on a scheduled time, return a time slot range */
const getExteriorAssessmentTimeslotFromLocalTime = (
  scheduledTime: LocalDateOrLocalDateTime,
  timeRange: AssessmentTimeRange,
): string => {
  if (!scheduledTime) {
    return '';
  } else if (scheduledTime instanceof LocalDateTime && timeRange === 'slot') {
    return scheduledTime.format(DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US));
  } else if (scheduledTime instanceof LocalDateTime && timeRange === 'two-hour') {
    const start = scheduledTime.format(DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US));
    const end = scheduledTime
      .plusHours(2)
      .format(DateTimeFormatter.ofPattern('h:mm a').withLocale(Locale.US));
    return `${start} - ${end}`;
  } else if (scheduledTime instanceof LocalDateTime && scheduledTime.hour() < 12) {
    return `8:00 AM - 12:00 PM`;
  }
  return `12:00 PM - 4:00 PM`;
};

/**
 * Formats a date with full day of week, month, day, and year (e.g., "Sunday, January 1, 2022")
 * @param dateTime - The LocalDateOrLocalDateTime to format
 * @returns Formatted date string
 */
export const formatDateWithFullDayOfWeek = (dateTime: LocalDate | LocalDateTime): string => {
  return dateTime.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT);
};

/**
 * Formats a date with month, day, and year (shorter format, e.g., "Jan 1, 2022")
 * @param dateTime - The LocalDateOrLocalDateTime to format
 * @returns Formatted date string
 */
export const formatDateShorter = (dateTime: LocalDate | LocalDateTime): string => {
  return dateTime.format(SHORTER_FULL_DATE_FORMAT);
};

/**
 * Formats a date with month, day, and year (full format, e.g., "January 1, 2022")
 * @param dateTime - The LocalDateOrLocalDateTime to format
 * @returns Formatted date string
 */
export const formatDateFull = (dateTime: LocalDate | LocalDateTime): string => {
  return dateTime.format(FULL_DATE_FORMAT);
};
